import { Pipe, PipeTransform } from '@angular/core';
import { ObjectHelper } from '@newroom-connect/library/helpers';

@Pipe({
  name: 'firstLetter',
  standalone: true
})
export class FirstLetterPipe implements PipeTransform {
  /**
   * Extracts the first letter from any input value.
   * Optionally returns '#' for numbers and special characters when useHashForNonLetters is true.
   *
   * @param value The input value to extract the first letter from (can be any type).
   * @param key Optional key to extract from an object (defaults to 'title'). Supports dot notation for nested properties.
   * @param useHashForNonLetters Optional flag to return '#' for non-letters (defaults to false).
   * @returns The first letter (A-Z), the original first character, or '#' based on configuration.
   *
   * @example
   * // Basic usage
   * transform('hello') // returns 'H'
   * transform(123) // returns '1'
   * transform(123, '', true) // returns '#'
   *
   * // Object usage
   * transform({ title: 'Test' }, 'title') // returns 'T'
   * transform({ user: { name: 'John' } }, 'user.name') // returns 'J'
   */
  public transform(value: unknown, key = 'title', useHashForNonLetters = false): string {
    if (value === null || value === undefined) {
      return useHashForNonLetters ? '#' : '';
    }

    return FirstLetterPipe.extractFirstLetterFromValue(value, key, useHashForNonLetters);
  }

  /**
   * Extracts text from a value, handling objects with nested property access.
   *
   * @param value The input value to extract text from.
   * @param key The key to use for object property extraction (supports dot notation).
   * @param useHashForNonLetters
   * @returns The extracted text as a string.
   */
  private static extractFirstLetterFromValue(value: unknown, key: string, useHashForNonLetters: boolean): string {
    const extractedValue = ObjectHelper.extractProperty(value, key);

    if (!extractedValue || typeof extractedValue !== 'string' || extractedValue.length === 0) {
      return useHashForNonLetters ? '#' : '';
    }

    const firstChar = extractedValue.charAt(0).toUpperCase();

    return useHashForNonLetters && !FirstLetterPipe.isLetter(firstChar)
      ? '#'
      : firstChar;
  }

  /**
   * Checks if a character is a letter (A-Z).
   *
   * @param char The character to check.
   *
   * @returns True if the character is a letter between A-Z.
   */
  private static isLetter(char: string): boolean {
    return char >= 'A' && char <= 'Z';
  }
}
